/**
 * RMS API client for Classic and Edge portal customizations and supported web font family names.
 */
// eslint-disable-next-line
import { injectable } from "inversify";
import { TYPE, rootContainer } from '../container/root';
import { IApiResponse, IHttpService } from "../http-service";
import { ICustomizationClassic, IEdgeCustomization, EdgeFontSlot } from "../../models/User";
import {WithOld} from "../../stores/types";

export type EdgeFontPackageSlot = EdgeFontSlot;

/** Family names returned by GET /rms-api/google-fonts (`payload` is `string[]`). */
export type PortalSupportedFontFamilyNames = string[];

@injectable()
class CustomizationsService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);

    public async loadCustomisations(): Promise<ICustomizationClassic> {
        const res = await this.httpService.get<IApiResponse<ICustomizationClassic>>(`/rms-api/customization`);
        return res.payload;
    }

    public async updateCustomizations(data: WithOld<ICustomizationClassic>): Promise<ICustomizationClassic> {
        const res = await this.httpService.put<IApiResponse<ICustomizationClassic>>(`/rms-api/customization`, data);
        return res.payload;
    };

    public async loadEdgeCustomizations(): Promise<IEdgeCustomization> {
        const res = await this.httpService.get<IApiResponse<IEdgeCustomization>>(`/rms-api/edge-customization`);
        return res.payload;
    }

    public async updateEdgeCustomizations(data: WithOld<IEdgeCustomization>): Promise<IEdgeCustomization> {
        const res = await this.httpService.put<IApiResponse<IEdgeCustomization>>(`/rms-api/edge-customization`, data);
        return res.payload;
    }

    /**
     * Multipart upload for a header or body font package (zip or single font file).
     */
    public async uploadEdgeFontPackage(slot: EdgeFontSlot, file: File): Promise<IEdgeCustomization> {
        const formData = new FormData();
        formData.append('slot', slot);
        formData.append('file', file);
        const res = await this.httpService.postFormData<IApiResponse<IEdgeCustomization>>(
            `/rms-api/edge-customization/font-package`,
            formData,
        );
        return res.payload;
    }

    public async getPortalSupportedFontFamilyNames(): Promise<PortalSupportedFontFamilyNames> {
        const res = await this.httpService.get<IApiResponse<PortalSupportedFontFamilyNames>>(
            `/rms-api/google-fonts`,
        );
        return res.payload;
    }
}

export default CustomizationsService;
